import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { useReactToPrint } from 'react-to-print';
import BackIcon from '../../common/Back/BackIcon';
import PageHeading from '../../common/Heading/PageHeading';
import { ContentTitle } from '../../ui/styled';
import Grid from '@mui/material/Grid';
import { LeftGrid } from '../../Episode/Episode.Styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import moment from 'moment';
import { getStopApi } from '../../../services/StopApi/StopApi';
import { ORG_INVOICE_DETAILS_URL } from '../../../constants';

const printStyles = `
  @media print {
    @page {
      size: landscape;
      margin: 10mm;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    .page-break {
      page-break-before: always !important;
      break-before: page !important;
    }

    table {
      border-collapse: collapse !important;
    }

    th, td {
      border: 1px solid #ddd !important;
    }
  }
`;

const headerCellSx = { color: '#fff', padding: '2px 4px', fontSize: '11px', fontWeight: 'bold' };
const cellSx = { padding: '2px 4px', fontSize: '11px' };
const totalCellSx = { color: '#fff', padding: '2px 4px', fontSize: '11px', fontWeight: 'bold' };

const TransportationInvoicePrintDialog = ({ openDialog, setOpenDialog, visibleInvIds, authToken }) => {
  const componentRef = useRef();
  const [loading, setLoading] = useState(false);
  const [invoiceGroups, setInvoiceGroups] = useState([]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
  });

  const handleClose = () => {
    setOpenDialog(false);
    setInvoiceGroups([]);
  };

  const fetchInvoiceDetails = async () => {
    if (!visibleInvIds || !authToken) return;
    setLoading(true);
    try {
      const url = `${ORG_INVOICE_DETAILS_URL}${visibleInvIds}`;
      const response = await getStopApi({ commonParams: url, authorization: authToken });
      const data = response?.data || [];
      // Group by orgInvId
      const grouped = {};
      data.forEach((line) => {
        const key = line.orgInvId;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(line);
      });
      setInvoiceGroups(Object.entries(grouped).map(([orgInvId, lines]) => ({
        orgInvId: Number(orgInvId),
        lines,
      })));
    } catch (e) {
      alert(e);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (openDialog && visibleInvIds) {
      fetchInvoiceDetails();
    }
  }, [openDialog]);

  const formatCurrency = (val) => {
    if (val == null) return '$0.00';
    return val.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const formatDate = (val) => {
    if (!val) return '';
    return moment(val).format('M/D/YYYY');
  };

  return (
    <Dialog fullScreen open={openDialog} onClose={handleClose}>
      <ContentTitle container>
        <Grid item xs={10}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <BackIcon />
            <PageHeading title='Transportation Invoice Details' />
          </Box>
        </Grid>
        <LeftGrid item xs={2}>
          <Button variant="contained" onClick={() => handlePrint()} disabled={loading || invoiceGroups.length === 0}>
            Print
          </Button>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </LeftGrid>
      </ContentTitle>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Box p={2} ref={componentRef}>
          {invoiceGroups.map((group, groupIndex) => {
            const firstLine = group.lines[0];
            let invoiceTotal = 0;
            return (
              <Box key={group.orgInvId} className={groupIndex > 0 ? 'page-break' : ''}>
                <Typography variant="body2" align="center" sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                  STOP SERVICES COORDINATION AGENCY (STOP) INVOICE
                </Typography>
                <Box display="flex" justifyContent="flex-end" mb={0}>
                  <Typography variant="body2" sx={{ fontSize: '12px' }}>
                    <strong>Invoice #:</strong> &nbsp;&nbsp;{group.orgInvId}
                  </Typography>
                </Box>

                <Typography variant="body1" align="center" sx={{ fontWeight: 'bold', fontSize: '14px', borderTop: '2px solid #000', borderBottom: '1px solid #000', py: 0.5 }}>
                  Transportation Detail
                </Typography>

                <Box display="flex" justifyContent="space-between" mb={1} mt={0.5}>
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                      <strong>Funding STOP Coordinator:</strong> &nbsp;{firstLine.tranForBillToOrgKey}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                      <strong>Transported by:</strong> &nbsp;{firstLine.tranByOrgKey}
                    </Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                      <strong>Org ID:</strong> {firstLine.tranForBillToOrgId}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '11px' }}>
                      <strong>Org ID:</strong> {firstLine.tranByOrgId}
                    </Typography>
                  </Box>
                </Box>

                <TableContainer component={Paper} elevation={0}>
                  <Table size="small" sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#747070' }}>
                        <TableCell sx={headerCellSx}></TableCell>
                        <TableCell sx={headerCellSx}>CDC #</TableCell>
                        <TableCell sx={headerCellSx}>Participant Name</TableCell>
                        <TableCell sx={headerCellSx}>Referral Source</TableCell>
                        <TableCell sx={headerCellSx} align="center">Region</TableCell>
                        <TableCell sx={headerCellSx} align="center">ICDTP Date</TableCell>
                        <TableCell sx={headerCellSx} align="right">Staff Reg Amount</TableCell>
                        <TableCell sx={headerCellSx} align="right">Staff Reg 1/4 Hr Rate</TableCell>
                        <TableCell sx={headerCellSx} align="center">Staff Reg 1/4 Hr Qty</TableCell>
                        <TableCell sx={headerCellSx} align="right">Staff O/T 1/4 Hr Rate</TableCell>
                        <TableCell sx={headerCellSx} align="center">Staff O/T 1/4 Hr Qty</TableCell>
                        <TableCell sx={headerCellSx} align="right">Mlg Rate</TableCell>
                        <TableCell sx={headerCellSx} align="right">Mlg Qty</TableCell>
                        <TableCell sx={headerCellSx} align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.lines.map((line, lineIndex) => {
                        invoiceTotal += line.activityAmount || 0;
                        // Parse participant name from key
                        const nameParts = line.activityForParticipantKey?.split(' (')[0] || '';
                        return (
                          <React.Fragment key={line.trnId}>
                            <TableRow sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                              <TableCell sx={cellSx}>{lineIndex + 1}</TableCell>
                              <TableCell sx={cellSx}>{line.cdcNumber}</TableCell>
                              <TableCell sx={cellSx}>{nameParts}</TableCell>
                              <TableCell sx={cellSx}>{line.fromOrgKey ? `NSG@${line.fromOrgKey.split('-')[0]}` : ''}</TableCell>
                              <TableCell sx={cellSx} align="center">{line.regionId}</TableCell>
                              <TableCell sx={cellSx} align="center">{formatDate(line.datesAct)}</TableCell>
                              <TableCell sx={cellSx} align="right">$0.00</TableCell>
                              <TableCell sx={cellSx} align="right">${line.staffTimeRate?.toFixed(2)}</TableCell>
                              <TableCell sx={cellSx} align="center">{line.timeAct || 0}</TableCell>
                              <TableCell sx={cellSx} align="right">${line.staffTimeOvertimeRate?.toFixed(2)}</TableCell>
                              <TableCell sx={cellSx} align="center">{line.staffTimeOvertimeQtyAct || 0}</TableCell>
                              <TableCell sx={cellSx} align="right">{line.mileageRate?.toFixed(2)}</TableCell>
                              <TableCell sx={cellSx} align="right">{line.mileageAct}</TableCell>
                              <TableCell sx={cellSx} align="right">{formatCurrency(line.activityAmount)}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ ...cellSx, borderBottom: '1px solid #ccc' }} colSpan={5}>
                                <Typography sx={{ fontSize: '10px', pl: 2 }}>
                                  {line.trnId} From: {line.fromOrgKey}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ ...cellSx, borderBottom: '1px solid #ccc' }} colSpan={4}>
                                <Typography sx={{ fontSize: '10px' }}>
                                  To: {line.toOrgKey || ''}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ ...cellSx, borderBottom: '1px solid #ccc' }} colSpan={5}>
                                <Typography sx={{ fontSize: '10px' }}>
                                  Outcome: {line.outcomeDescription}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                      <TableRow sx={{ backgroundColor: '#747070' }}>
                        <TableCell sx={totalCellSx} colSpan={5}>
                          Transport by {firstLine.tranByOrgKey} Total:
                        </TableCell>
                        <TableCell sx={totalCellSx} align="right">$0.00</TableCell>
                        <TableCell sx={totalCellSx} align="right">$0.00</TableCell>
                        <TableCell sx={totalCellSx} align="center">0</TableCell>
                        <TableCell sx={totalCellSx} align="right">$0.00</TableCell>
                        <TableCell sx={totalCellSx} align="center">0</TableCell>
                        <TableCell sx={totalCellSx} align="right">0.54</TableCell>
                        <TableCell sx={totalCellSx} align="right">
                          {group.lines.reduce((sum, l) => sum + (l.mileageAct || 0), 0)}
                        </TableCell>
                        <TableCell sx={totalCellSx} align="right" colSpan={2}>
                          {formatCurrency(invoiceTotal)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box mt={2}>
                  <Typography variant="body2" sx={{ fontSize: '10px' }}>
                    TRANSPORTED BY &nbsp;PROGRAM COORDINATOR &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; DATE (mm/dd/yyyy)
                  </Typography>
                  <Box mt={1}>
                    <Typography variant="body2" sx={{ fontSize: '10px' }}>
                      APPROVED BY &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; DATE (mm/dd/yyyy)
                    </Typography>
                  </Box>
                </Box>

                <Box mt={2} display="flex" justifyContent="space-between" borderTop="1px solid #ccc" pt={0.5}>
                  <Typography variant="body2" sx={{ fontSize: '9px' }}>
                    {moment().format('M/D/YYYY h:mm:ss A')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '9px' }}>
                    Phone: 559-251-4800 &nbsp;&nbsp; Fax: {process.env.REACT_APP_STOP_REGION_FAX || '559-453-6969'}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Dialog>
  );
};

export default TransportationInvoicePrintDialog;
